import { useTranslations } from '@vocab/react';
import {
  Text,
  Badge,
  Stack,
  IconAdd,
  IconEdit,
  ButtonIcon,
  Columns,
  Column,
  Box,
} from 'braid-design-system';
import type { ReactNode } from 'react';

import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { EDIT } from 'src/consts';
import { analyticsClient } from 'src/modules/analytics';

import { NextRoleSection } from '../../types';

import translations from './.vocab';

interface NextRoleDetailProps {
  title: string;
  children?: ReactNode;
  section: NextRoleSection;
  showNewBadge?: boolean;
  overrideAddButton?: boolean;
}

const mapNextRoleSectionToAnalyticsCurrentPageSubsection = {
  [NextRoleSection.availability]: 'availability',
  [NextRoleSection.workTypes]: 'work type',
  [NextRoleSection.preferredLocations]: 'location',
  [NextRoleSection.rightToWork]: 'right to work',
  [NextRoleSection.salary]: 'salary',
  [NextRoleSection.classification]: 'classification',
  [NextRoleSection.approachability]: 'approachability',
} as const;

export const NextRoleDetail = ({
  title,
  children,
  section,
  showNewBadge = false,
  overrideAddButton = false,
}: NextRoleDetailProps) => {
  const { t } = useTranslations(translations);
  const navigate = useNavigateRoute();
  const hasDetails = typeof children !== 'undefined' && children !== false;
  const details = hasDetails ? (
    <>{children}</>
  ) : (
    <Text tone="secondary">{t('Not specified')}</Text>
  );

  const isAdd = !hasDetails || overrideAddButton;

  const onClick = () => {
    analyticsClient.cardOpened({
      currentPageSection: 'next role',
      currentPageSectionState: isAdd ? 'add' : 'edit',
      currentPageSubsection:
        mapNextRoleSectionToAnalyticsCurrentPageSubsection[section],
    });
    navigate({
      card: section,
      mode: EDIT,
    });
  };

  return (
    <Columns space="none">
      <Column>
        <Stack space="medium">
          <Text weight="strong">
            {title}
            {showNewBadge ? (
              <Box component="span" paddingLeft="xsmall">
                <Badge tone="positive">{t('New')}</Badge>
              </Box>
            ) : null}
          </Text>
          {details}
        </Stack>
      </Column>
      <Column width="content">
        {isAdd ? (
          <ButtonIcon
            id={`add-${section}`}
            onClick={onClick}
            icon={<IconAdd />}
            variant="transparent"
            label={t('Add title', { title })}
            data={{ automation: `${section}-add-button` }}
          />
        ) : (
          <ButtonIcon
            id={`edit-${section}`}
            onClick={onClick}
            icon={<IconEdit />}
            variant="transparent"
            label={t('Edit title', { title })}
            data={{ automation: `${section}-edit-button` }}
          />
        )}
      </Column>
    </Columns>
  );
};
